import { h, Component } from 'preact';
import * as transitionStates from './transitionStates';
// import isShallowEqual from 'shallow-equals';
import { joinClasses } from 'utils/utils';
import pendingPromise from 'utils/pendingPromise';

import s from 'components/core/transition/transition.sss';

const isServer = typeof window === 'undefined';

/**
* Transition between children changing
* All the children involved should have an id or key set as a property!
* @prop {string} class - The class on the transitionWrapper
* @prop {Number} duration[!] - The duration for the whole transitionWrapper
* @prop {string} enteringClass - Extra class added when child is entering
* @prop {string} leavingClass - Extra class added when child is leaving
* @prop {string} visibleClass - Extra class added when child is visible
* @prop {string} emptyClass - Extra class added once child has left
*/
export default class Transition extends Component {

	constructor(props) {
		super(props);
		let childGenerator = this.getChildGenerator(props);
		// let children = childGenerator();

		let initialState = {
			firstChild: childGenerator,
			firstChildState: transitionStates.STATE_CHILD_ENTERING,
			secondChild: null,
			secondChildState: transitionStates.STATE_CHILD_EMPTY,
			upcomingChild: childGenerator
		};

		let disableInit = this.props.noInitialTransition;
		if (disableInit) {
			initialState.firstChildState = transitionStates.STATE_CHILD_VISIBLE;
		}
		this.state = initialState;

		if (!disableInit) {
			this.setTimer(() => {
				this.setState({
					firstChildState: this.getNextStep('firstChild', this.state.firstChildState)
				});
			}, props.duration / 2);
		}
	}

	componentWillReceiveProps({ waitBeforeNext, ...props }) {
		let childGenerator = this.getChildGenerator(props);
		let child = childGenerator();

		const newChildId = this.getId(child);
		const oldChildId = this.getId(this.state.upcomingChild());

		if (newChildId !== oldChildId) {
			this.setState({ upcomingChild: childGenerator });
			let firstVisible = this.state.firstChildState === transitionStates.STATE_CHILD_VISIBLE;
			let secondVisible = this.state.secondChildState === transitionStates.STATE_CHILD_VISIBLE;
			if (!waitBeforeNext || firstVisible || secondVisible) {
				this.goToNextPage();
			} else {
				this.afterTimer = this.goToNextPage;
			}
		} else {
			// Update the current child so it gets it new props
			if (this.state.firstChild && this.getId(this.state.firstChild()) === newChildId) {
				this.setState({ firstChild: childGenerator });
			}
			if (this.state.secondChild && this.getId(this.state.secondChild()) === newChildId) {
				this.setState({ secondChild: childGenerator });
			}

		}
	}

	componentDidUpdate() {
		this.renderDone?.resolve();
		this.renderDone = null;
	}

	getChildGenerator(props) {
		return typeof props.renderChildren === 'function' ? props.renderChildren : () => props.children[0];
	}

	getId(vNode) {
		return vNode?.attributes?.id || vNode?.attributes?.key;
	}

	getStyles = () => {
		return {
			'transition-duration': (this.props.duration / 2) + 'ms',
			'animation-duration': (this.props.duration / 2) + 'ms'
		};
	};

	async goToNextPage() {
		this.endTimer();

		const {
			firstChild,
			secondChild,
			upcomingChild,
			firstChildState,
			secondChildState
		} = this.state;

		// Ugly quickfix to prevent same -> same transition
		const firstChildVisible = firstChildState === transitionStates.STATE_CHILD_VISIBLE;
		const secondChildVisible = secondChildState === transitionStates.STATE_CHILD_VISIBLE;


		if (firstChildVisible && this.getId(upcomingChild()) === this.getId(firstChild()) ||
			secondChildVisible && this.getId(upcomingChild()) === this.getId(secondChild())) {
			return;
		}

		this.setState({
			firstChildState: this.getNextStep('firstChild', firstChildState),
			secondChildState: this.getNextStep('secondChild', secondChildState)
		});
		let next = () => {
			this.setState({
				firstChildState: this.getNextStep('firstChild', this.state.firstChildState),
				secondChildState: this.getNextStep('secondChild', this.state.secondChildState)
			});
		};

		this.renderDone?.resolve();
		this.renderDone = pendingPromise();
		await this.renderDone;
		this.setTimer(next, this.props.duration / 2);
	}

	getNextStep(childType, currentState) {
		let newState = {};

		switch (currentState) {
			case transitionStates.STATE_CHILD_ENTERING:
				return transitionStates.STATE_CHILD_VISIBLE;

			case transitionStates.STATE_CHILD_VISIBLE:
				return transitionStates.STATE_CHILD_LEAVING;

			case transitionStates.STATE_CHILD_LEAVING:
				newState[childType] = null;
				this.setState(newState);
				return transitionStates.STATE_CHILD_EMPTY;

			case transitionStates.STATE_CHILD_EMPTY:
				newState[childType] = this.state.upcomingChild;
				this.setState(newState);
				return transitionStates.STATE_CHILD_ENTERING;
		}
	}

	getActiveClass(currentState) {
		let { enteringClass, visibleClass, leavingClass, emptyClass } = this.props;
		let entering = currentState === transitionStates.STATE_CHILD_ENTERING;
		let visible = currentState === transitionStates.STATE_CHILD_VISIBLE;
		let leaving = currentState === transitionStates.STATE_CHILD_LEAVING;
		let empty = currentState === transitionStates.STATE_CHILD_EMPTY;
		return joinClasses(
			this.props.baseClass,
			entering && enteringClass,
			visible && visibleClass,
			leaving && leavingClass,
			empty && emptyClass,
			{
				s,
				entering,
				visible,
				leaving,
				empty
			}
		);
	}

	setTimer = (f, d) => {
		this.endTimer();
		this.timerFunc = f;
		this.timer = setTimeout(() => {
			f();
			this.timer = null;
			if (this.afterTimer) {
				this.afterTimer();
				this.afterTimer = null;
			}
		}, d);
	};

	endTimer = (preventExecution) => {
		if (!this.timer) return;
		clearTimeout(this.timer);

		// Call the function early
		if (!preventExecution) {
			this.timerFunc();
		}

		this.timer = null;
		if (this.afterTimer) {
			this.afterTimer();
			this.afterTimer = null;
		}
	};

	render({ duration, ...props }, { firstChild, firstChildState, secondChild, secondChildState }) {
		// let child = this.getChildGenerator(props)();
		let firstChildNode;
		let secondChildNode;

		let styles = this.getStyles();

		firstChild = firstChild && firstChild();
		// Set the current state
		if (firstChild?.attributes && typeof firstChild?.nodeName !== 'string') {
			firstChild.attributes.transitionState = isServer ? transitionStates.STATE_CHILD_VISIBLE : firstChildState;
		}

		secondChild = secondChild && secondChild();
		if (secondChild?.attributes && typeof secondChild?.nodeName !== 'string') {
			secondChild.attributes.transitionState = secondChildState;
		}

		let firstChildClass = this.getActiveClass(isServer ? transitionStates.STATE_CHILD_VISIBLE : firstChildState);
		firstChildNode = <div class={firstChildClass} style={styles}>{firstChild}</div>;

		let secondChildClass = this.getActiveClass(secondChildState);
		secondChildNode = <div class={secondChildClass} style={styles}>{secondChild}</div>;

		return (
			<div class={props.class} id={props.id}>
				{firstChildNode}
				{secondChildNode}
			</div>
		);
	}

}
